import { Box } from '@mui/material';
import { DropDownText } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/comm-preferences.styles';
import { getDefaultFamilyMember } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/utils/get-default-family-member';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import { PatientLanguage } from 'apps/wrapper/src/services/api-client/contact-api';
import { FC, useMemo } from 'react';
import { AppointmentSection, Dropdown, Section, SectionItem, SectionTitle, Separator } from './comm-preferences.styles';

const langOptions: PatientLanguage[] = [PatientLanguage.English, PatientLanguage.Spanish];

type CommPreferencesGeneralViewProps = {
  setChange: (key: string, value: any) => void;
  changes: any;
};

export const CommPreferencesGeneralView: FC<CommPreferencesGeneralViewProps> = ({ setChange, changes }) => {
  const { patientPreferences, patientInfo } = usePatientProfile();

  const defaultsPreferences = useMemo(() => {
    return {
      defaultFamilyMember:
        changes.updateFamilyOptions || patientInfo.familyMembers
          ? getDefaultFamilyMember(patientPreferences, patientInfo.familyMembers || [])
          : null,
      defaultLanguage: changes.updateLanguageOptions || patientPreferences.language
    };
  }, [patientPreferences, patientInfo, changes]);

  const updateLocalPreferences = (state: Partial<typeof patientPreferences>) => {
    const newState = { ...changes.updateLocalPreferences, ...state };
    setChange('updateLocalPreferences', newState);
  };

  return (
    <div>
      <Section>
        {patientInfo.familyMembers && (
          <SectionItem>
            <SectionTitle>Family Messaging</SectionTitle>
            <Dropdown
              autoComplete={false}
              disableClearable
              sx={{ WebkitTextFillColor: '#000000' }}
              disablePortal
              defaultValue={defaultsPreferences.defaultFamilyMember}
              size="small"
              id="family-options-dropdown"
              options={patientInfo.familyMembers}
              onChange={(item, value: any) => setChange('updateFamilyOptions', value.id)}
              getOptionLabel={(option: any) =>
                `${option.firstName} ${option.lastName} ${option.isResponsibleParty ? ' (RP)' : ''}`
              }
              renderOption={(props, option: any) => (
                <Box component="li" {...props} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
                  {`${option?.firstName} ${option?.lastName} ${option?.isResponsibleParty ? ' (RP)' : ''}`}
                </Box>
              )}
              renderInput={(params) => (
                <DropDownText {...params} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }} variant="outlined" />
              )}
            />
          </SectionItem>
        )}
        <SectionItem>
          <SectionTitle>Preferred Language</SectionTitle>
          <Dropdown
            autoComplete={false}
            disableClearable
            sx={{ WebkitTextFillColor: '#000000' }}
            disablePortal
            defaultValue={defaultsPreferences.defaultLanguage}
            size="small"
            id="language-options-dropdown"
            options={langOptions}
            onChange={(item, value: any) => setChange('updateLanguageOptions', value)}
            getOptionLabel={(option: any) => option}
            renderOption={(props, option: any) => (
              <Box component="li" {...props} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
                {option}
              </Box>
            )}
            renderInput={(params) => <DropDownText {...params} variant="outlined" />}
          />
        </SectionItem>
        <Separator>Appointment Reminders</Separator>
        <AppointmentSection
          header="Save the Date"
          preference={patientPreferences.saveDateReminderScheduleStatus}
          onChange={(value) =>
            updateLocalPreferences({
              saveDateReminderScheduleStatus: value
            })
          }
        />
        <AppointmentSection
          header="Postcard"
          preference={patientPreferences.postCardReminderScheduleStatus}
          onChange={(value) =>
            updateLocalPreferences({
              postCardReminderScheduleStatus: value
            })
          }
        />
        <AppointmentSection
          header="First Reminder"
          preference={patientPreferences.firstReminderScheduleStatus}
          onChange={(value) =>
            updateLocalPreferences({
              firstReminderScheduleStatus: value
            })
          }
        />
        <AppointmentSection
          header="Second Reminder"
          preference={patientPreferences.secondReminderScheduleStatus}
          onChange={(value) =>
            updateLocalPreferences({
              secondReminderScheduleStatus: value
            })
          }
        />
        <AppointmentSection
          header={'Same Day Reminder'}
          preference={patientPreferences.sameDayReminderScheduleStatus}
          onChange={(value) =>
            updateLocalPreferences({
              sameDayReminderScheduleStatus: value
            })
          }
        />
      </Section>
    </div>
  );
};
